/**
 * Google Analytics (gtag.js) thin wrapper.
 *
 * Queues events when `gtag` not yet loaded; flushes once available.
 * Replaces `window.gtag` during tests to avoid network calls.
 *
 * @module utils/analytics
 */

import { isMobileDevice, detectPlatform as centralizedDetectPlatform } from '../engine/device.js';
import { useFeatureFlags } from '../engine/featureFlags.js'; // access canonical feature state

// Google Analytics measurement ID – allow override via Vite env
const GA_MEASUREMENT_ID = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_GA_MEASUREMENT_ID) || 'G-BHG5FC0KM8';

// Initialize Google Analytics script
function initializeGoogleAnalytics() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  
  // Check if GA script is already loaded
  if (document.querySelector(`script[src*="https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`)) {
    return;
  }
  
  // Create and append the gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
  
  // Initialize the gtag dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  
  // Initial gtag configuration
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false // We handle page views manually
  });
  
  if (typeof __DEV__ !== 'undefined' && __DEV__) {
    console.log('[Analytics] Google Analytics initialized with ID:', GA_MEASUREMENT_ID);
  }
}

// Helper: compute enabled feature list as comma-separated string for GA user properties
function _getEnabledFeaturesCsv() {
  try {
    const flags = useFeatureFlags?.getState?.().flags || [];
    return Array.isArray(flags) ? flags.join(',') : '';
  } catch {
    return '';
  }
}

// Push canonical user properties (feature flags, env, platform) to GA4
function _setUserProperties() {
  if (typeof window === 'undefined' || typeof window.gtag === 'undefined') return;
  window.gtag('set', 'user_properties', {
    enabled_features: _getEnabledFeaturesCsv(),
    app_env: (typeof import.meta !== 'undefined' && import.meta.env?.APP_ENVIRONMENT) || (typeof process !== 'undefined' && process.env?.APP_ENVIRONMENT) || 'development',
  });
}

// Session tracking
let sessionId = null;
let sessionStartTime = null;
let lastActivityTime = null;

// Generate unique session ID
function generateSessionId() {
  return `dcw_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Initialize session tracking
function initializeSession() {
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStartTime = Date.now();
    lastActivityTime = Date.now();
    
    // Track session start
    trackEvent('session_start', {
      session_id: sessionId,
      timestamp: sessionStartTime,
      user_agent: navigator.userAgent,
      screen_resolution: `${screen.width}x${screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      platform: detectPlatform(),
      is_mobile: isMobileDevice(),
      is_mini_app: isMiniApp()
    });
  }
}

// Platform detection for analytics – use centralized detection from device.js
function detectPlatform() {
  try {
    // Use the centralized platform detection
    return centralizedDetectPlatform();
  } catch (error) {
    // Fallback to basic detection if centralized detection fails
    if (typeof navigator === 'undefined') return 'unknown';
    const userAgent = (navigator.userAgent || '').toLowerCase();

    if (userAgent.includes('farcaster')) return 'farcaster';
    if (userAgent.includes('coinbase')) return 'coinbase_wallet';
    if (userAgent.includes('worldapp')) return 'world_app';
    if (userAgent.includes('mobile')) return 'mobile_web';
    return 'desktop_web';
  }
}

// Mini-app detection – guard for missing window
function isMiniApp() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  return window.parent !== window || 
         navigator.userAgent.includes('farcaster') || 
         navigator.userAgent.includes('coinbase') ||
         navigator.userAgent.includes('worldapp');
}

// Core tracking function
export function trackEvent(eventName, parameters = {}) {
  // Check if window is available
  if (typeof window === 'undefined') {
    console.warn('[Analytics] Window not available, skipping event:', eventName);
    return;
  }
  
  // Initialize GA if not already done
  if (typeof window.gtag === 'undefined') {
    // Queue event for later processing
    if (!window.pendingAnalyticsEvents) window.pendingAnalyticsEvents = [];
    window.pendingAnalyticsEvents.push({ eventName, parameters });
    
    // Try to initialize Google Analytics
    try {
      initializeGoogleAnalytics();
    } catch (e) {
      console.warn('[Analytics] Failed to initialize Google Analytics:', e.message);
    }
    return;
  }

  // Update last activity time
  lastActivityTime = Date.now();

  // Add session context to all events
  const enrichedParams = {
    ...parameters,
    session_id: sessionId,
    session_duration: getSessionDuration(),
    platform: detectPlatform(),
    is_mobile: isMobileDevice(),
    is_mini_app: isMiniApp(),
    wallet_type: getCurrentWalletType(),
  };

  try {
    // Send event to GA
    window.gtag('event', eventName, enrichedParams);
  } catch (e) {
    console.error('[Analytics] Error sending event:', e.message);
  }
}

// Get current wallet type
function getCurrentWalletType() {
  // This would integrate with your auth system
  try {
    if (typeof window === 'undefined') return 'unknown';
    
    // Try to get from global auth functions
    if (window.getCurrentUser && typeof window.getCurrentUser === 'function') {
      const currentUser = window.getCurrentUser() || {};
      return currentUser.walletType || currentUser.platform || 'unknown';
    }
    
    // Fallback to platform detection
    return detectPlatform();
  } catch {
    return 'unknown';
  }
}

// === GAME DEVELOPMENT SPECIFIC TRACKING ===

// 1. User Journey & Onboarding
export function trackUserJourney(step, data = {}) {
  trackEvent('user_journey', {
    journey_step: step,
    ...data
  });
}

// 2. Authentication & Wallet Connection
export function trackAuth(action, data = {}) {
  trackEvent('auth_action', {
    auth_action: action,
    ...data
  });
}

// 3. Chat & AI Interaction
export function trackChatInteraction(action, data = {}) {
  trackEvent('chat_interaction', {
    chat_action: action,
    character: data.character || 'unknown',
    message_length: data.messageLength || 0,
    response_time: data.responseTime || 0,
    conversation_length: data.conversationLength || 0,
    ...data
  });
}

// 4. UI/UX Performance & Engagement
export function trackUIInteraction(component, action, data = {}) {
  trackEvent('ui_interaction', {
    component: component,
    action: action,
    ...data
  });
}

// 5. Technical Performance
export function trackPerformance(metric, value, data = {}) {
  trackEvent('performance_metric', {
    metric_name: metric,
    metric_value: value,
    ...data
  });
}

// 6. Error Tracking
export function trackError(errorType, error, additionalParams = {}) {
  const safeWindow = (typeof window !== 'undefined') ? window : {};
  const safeNavigator = (typeof navigator !== 'undefined') ? navigator : {};
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: error.message || String(error),
    error_stack: error.stack || '',
    context: JSON.stringify(additionalParams),
    url: safeWindow.location ? safeWindow.location.href : '',
    user_agent: safeNavigator.userAgent || 'unknown'
  });
}

// 7. Business Metrics
export function trackBusinessMetric(metric, value, data = {}) {
  trackEvent('business_metric', {
    metric_name: metric,
    metric_value: value,
    ...data
  });
}

// 8. Feature Usage
export function trackFeatureUsage(feature, action, data = {}) {
  trackEvent('feature_usage', {
    feature_name: feature,
    feature_action: action,
    ...data
  });
}

// === AUTOMATIC TRACKING ===

// Page view tracking
export function trackPageView(pageName, additionalParams = {}) {
  initializeSession();
  trackEvent('page_view', {
    page_name: pageName,
    page_url: typeof window !== 'undefined' ? window.location.href : '',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    ...additionalParams
  });
}

// Session duration tracking
function trackSessionDuration() {
  if (sessionStartTime) {
    const duration = Math.floor((Date.now() - sessionStartTime) / 1000);
    trackEvent('session_duration', {
      duration_seconds: duration,
      session_id: sessionId
    });
  }
}

// Track when user becomes inactive
let inactivityTimer = null;
function resetInactivityTimer() {
  if (inactivityTimer) clearTimeout(inactivityTimer);
  
  inactivityTimer = setTimeout(() => {
    trackEvent('user_inactive', {
      inactive_duration: Math.floor((Date.now() - lastActivityTime) / 1000),
      session_id: sessionId
    });
  }, 300000); // 5 minutes of inactivity
}

// PHANTOM CODE ELIMINATED: Track listeners for cleanup
let activityListeners = [];
let visibilityListener = null;
let beforeUnloadListener = null;

// Auto-track user activity with proper cleanup capability
function setupActivityTracking() {
  // PHANTOM CODE ELIMINATED: Track all listeners for proper cleanup
  ['click', 'keydown', 'scroll', 'touchstart'].forEach(event => {
    const listener = () => resetInactivityTimer();
    document.addEventListener(event, listener, { passive: true });
    activityListeners.push({ event, listener });
  });
  
  // Track when user leaves/returns
  visibilityListener = () => {
    if (document.hidden) {
      trackEvent('page_hidden', { session_id: sessionId });
    } else {
      trackEvent('page_visible', { session_id: sessionId });
    }
  };
  document.addEventListener('visibilitychange', visibilityListener);
  
  // Track session end on page unload
  beforeUnloadListener = () => {
    trackSessionDuration();
    trackEvent('session_end', { session_id: sessionId });
  };
  window.addEventListener('beforeunload', beforeUnloadListener);
}

/**
 * PHANTOM CODE ELIMINATED: Add cleanup function for analytics to prevent memory leaks
 */
export function cleanupAnalytics() {
  // Clear inactivity timer
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }
  
  // Remove activity listeners
  activityListeners.forEach(({ event, listener }) => {
    document.removeEventListener(event, listener);
  });
  activityListeners = [];
  
  // Remove visibility listener
  if (visibilityListener) {
    document.removeEventListener('visibilitychange', visibilityListener);
    visibilityListener = null;
  }
  
  // Remove beforeunload listener
  if (beforeUnloadListener) {
    window.removeEventListener('beforeunload', beforeUnloadListener);
    beforeUnloadListener = null;
  }
  
  console.log('[Analytics] 🧹 Cleanup completed');
}

// === GAME-SPECIFIC METRICS ===

// CRT/Visual Performance
export function trackCRTPerformance(fps, renderTime, data = {}) {
  trackPerformance('crt_performance', fps, {
    render_time_ms: renderTime,
    ...data
  });
}

// Wallet Integration Success Rate
export function trackWalletSuccess(walletType, action, success, data = {}) {
  trackBusinessMetric('wallet_success_rate', success ? 1 : 0, {
    wallet_type: walletType,
    wallet_action: action,
    ...data
  });
}

// Chat Response Quality (user satisfaction proxy)
export function trackChatQuality(rating, character, data = {}) {
  trackBusinessMetric('chat_quality', rating, {
    character: character,
    ...data
  });
}

// User Retention Markers
export function trackRetentionMarker(milestone, data = {}) {
  trackBusinessMetric('retention_milestone', 1, {
    milestone: milestone,
    ...data
  });
}

// === INITIALIZATION ===
// Only run DOM/event wiring when full browser APIs are present
const _hasDomApis = typeof window !== 'undefined' && typeof document !== 'undefined' &&
                    typeof document.addEventListener === 'function' &&
                    typeof window.addEventListener === 'function';

// -----------------------------------------------------------------------------
//  GA helper functions (only meaningful in browser, guarded by _hasDomApis)
// -----------------------------------------------------------------------------
function processPendingEvents() {
  if (!_hasDomApis) return;
  if (window.pendingAnalyticsEvents && window.gtag) {
    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      console.log('[Analytics] Processing', window.pendingAnalyticsEvents.length, 'pending events');
    }
    window.pendingAnalyticsEvents.forEach(({ eventName, parameters }) => {
      trackEvent(eventName, parameters);
    });
    window.pendingAnalyticsEvents = [];
  }
}

function waitForGtag() {
  if (!_hasDomApis) return;
  
  // First, attempt to initialize Google Analytics if not already loaded
  initializeGoogleAnalytics();
  
  if (typeof window.gtag !== 'undefined') {
    if (typeof __DEV__ !== 'undefined' && __DEV__) console.log('[Analytics] gtag is ready');

    // Verify GA configuration
    window.gtag('config', GA_MEASUREMENT_ID, {
      debug_mode: true,
      send_page_view: false, // We handle page views manually
    });

    // Send initial user properties once GA is ready
    _setUserProperties();
    processPendingEvents();
    return;
  }

  // Poll for gtag availability for up to 10 seconds
  let attempts = 0;
  const checkInterval = setInterval(() => {
    // Guard for headless test environments where global.window might be deleted
    if (typeof window === 'undefined') {
      clearInterval(checkInterval);
      return;
    }
    attempts++;
    
    // Try initializing Google Analytics again if still not available
    if (attempts % 10 === 0) {
      initializeGoogleAnalytics();
    }
    
    if (typeof window.gtag !== 'undefined') {
      if (typeof __DEV__ !== 'undefined' && __DEV__) {
        console.log('[Analytics] gtag loaded after', attempts * 100, 'ms');
      }
      clearInterval(checkInterval);

      window.gtag('config', GA_MEASUREMENT_ID, {
        debug_mode: true,
        send_page_view: false,
      });

      _setUserProperties();
      processPendingEvents();
    } else if (attempts >= 100) {
      console.warn('[Analytics] gtag failed to load after 10 seconds');
      clearInterval(checkInterval);
    }
  }, 100);
}

if (_hasDomApis) {
  // Initialize Google Analytics as early as possible
  initializeGoogleAnalytics();
  
  // Wait for gtag to be available
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForGtag);
  } else {
    waitForGtag();
  }

  // Global error handler for debugging
  window.addEventListener('error', (event) => {
    trackError('javascript_error', event.error || new Error(event.message), {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack
    });
  });

  // Promise rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    trackError('promise_rejection', event.reason, {
      promise: 'unhandled'
    });
  });

  // Setup automatic tracking
  setupActivityTracking();
  
  // Track initial page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      trackPageView('app_load');
    });
  } else {
    trackPageView('app_load');
  }
}

// -----------------------------------------------------------------------------
// Watch for feature-flag updates and push to GA user properties in real-time.
// This ensures dashboards always reflect the *current* canonical state.
// -----------------------------------------------------------------------------
try {
  if (useFeatureFlags?.subscribe) {
    useFeatureFlags.subscribe((state) => {
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('set', 'user_properties', {
          enabled_features: Array.isArray(state.flags) ? state.flags.join(',') : '',
        });
      }
    });
  }
} catch {/* ignore – store not initialised in tests */}

// Export session utilities
export function getSessionId() {
  return sessionId;
}

export function getSessionDuration() {
  return sessionStartTime ? Math.floor((Date.now() - sessionStartTime) / 1000) : 0;
}

// === GDPR COMPLIANCE ===
export function setAnalyticsConsent(consent) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: consent ? 'granted' : 'denied'
    });
  }
}

// === DEBUGGING & TESTING ===
export function testAnalytics() {
  if (typeof __DEV__ !== "undefined" && __DEV__) console.log('[Analytics Test] Starting...');
  
  // Test basic event
  trackEvent('analytics_test', {
    test_type: 'manual_test',
    timestamp: Date.now()
  });
  
  // Test page view
  trackPageView('test_page', {
    test_data: 'analytics_verification'
  });
  
  // Test user journey
  trackUserJourney('test_step', {
    test_user: 'analytics_test'
  });
  
  if (typeof __DEV__ !== "undefined" && __DEV__) console.log('[Analytics Test] Events sent. Check browser network tab for requests to google-analytics.com');
  if (typeof __DEV__ !== "undefined" && __DEV__) console.log('[Analytics Test] Session ID:', getSessionId());
  if (typeof __DEV__ !== "undefined" && __DEV__) console.log('[Analytics Test] Platform:', detectPlatform());
  if (typeof __DEV__ !== "undefined" && __DEV__) console.log('[Analytics Test] Wallet Type:', getCurrentWalletType());
}

// Make test function available globally for debugging
if (typeof window !== 'undefined') {
  window.testAnalytics = testAnalytics;
} 